import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Target, Plus, X } from "lucide-react";

const ACTIONS = [
  { value: "chat", label: "Send chats" },
  { value: "watch_minute", label: "Watch minutes" },
  { value: "game_played", label: "Games played" },
  { value: "game_won", label: "Games won" },
  { value: "tip_per_credit", label: "Tip credits" },
];

export default function ChallengesCard({ channelUsername }) {
  const [challenges, setChallenges] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", action_type: "chat", target: 5, reward_credits: 25 });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/channels/${channelUsername}/challenges`).then(r => setChallenges(r.data || [])).catch(() => {});
  };

  useEffect(() => { if (channelUsername) load();   }, [channelUsername]);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || form.target <= 0) return;
    setBusy(true);
    try {
      await api.post(`/channels/${channelUsername}/challenges`, form);
      setForm({ name: "", description: "", action_type: "chat", target: 5, reward_credits: 25 });
      load();
    } finally { setBusy(false); }
  };

  const end = async (id) => {
    if (!window.confirm("End this challenge?")) return;
    try { await api.delete(`/channels/${channelUsername}/challenges/${id}`); load(); } catch { /* */ }
  };

  return (
    <div className="sc-card p-6 space-y-4" data-testid="challenges-card">
      <div className="flex items-center gap-2">
        <Target className="w-5 h-5 text-[color:var(--sc-pink)]" />
        <div className="sc-heading text-xl font-bold">Community Challenges</div>
        <span className="ml-auto text-[10px] sc-mono tracking-widest uppercase px-2 py-1 rounded-sm" style={{ background: "var(--sc-pink)", color: "#0A0A0C" }}>ENGAGE</span>
      </div>
      <p className="text-xs text-zinc-400">Give viewers goals inside your community. Auto-tracked. Completion rewards credits + XP.</p>

      <form onSubmit={create} className="grid sm:grid-cols-2 gap-3 p-3 border border-white/5 rounded-sm">
        <div className="sm:col-span-2">
          <label className="sc-label block mb-1">Name</label>
          <input data-testid="challenge-name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Send 10 chats tonight" className="sc-input" required />
        </div>
        <div>
          <label className="sc-label block mb-1">Action</label>
          <select data-testid="challenge-action" value={form.action_type} onChange={e => setForm({...form, action_type: e.target.value})} className="sc-input">
            {ACTIONS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div>
          <label className="sc-label block mb-1">Target</label>
          <input data-testid="challenge-target" type="number" min={1} value={form.target} onChange={e => setForm({...form, target: Number(e.target.value)})} className="sc-input" />
        </div>
        <div>
          <label className="sc-label block mb-1">Reward (credits)</label>
          <input data-testid="challenge-reward" type="number" min={0} value={form.reward_credits} onChange={e => setForm({...form, reward_credits: Number(e.target.value)})} className="sc-input" />
        </div>
        <div>
          <label className="sc-label block mb-1">Short description</label>
          <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Optional" className="sc-input" />
        </div>
        <div className="sm:col-span-2">
          <button data-testid="challenge-create" disabled={busy} className="sc-btn flex items-center justify-center gap-1.5"><Plus className="w-4 h-4" />{busy ? "Creating…" : "Create Challenge"}</button>
        </div>
      </form>

      {challenges.length > 0 && (
        <div>
          <div className="sc-label mb-2">Active ({challenges.length})</div>
          <div className="space-y-2">
            {challenges.map(c => (
              <div key={c.id} className="flex items-center justify-between text-xs p-3 border border-white/5 rounded-sm" data-testid={`challenge-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-zinc-200">{c.name}</div>
                  <div className="text-zinc-500 sc-mono">{c.action_type} · target {c.target} · +{c.reward_credits}c</div>
                </div>
                <button onClick={() => end(c.id)} className="p-1.5 border border-white/10 rounded-sm hover:bg-white/5" data-testid={`challenge-end-${c.id}`} title="End challenge"><X className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
